import { Assert } from '@aura/core-lib';
import { AuthenticationResult, InteractionStatus, ServerError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo } from 'react';
import { ConfigsManager } from '../../infra/configs/configs.manager';
import { candidato, portal } from '../../infra/configs/environment/environment.configs';
import { resultadoAutenticacaoAtualizado } from '../../stores/autenticacao/autenticacao.store';
import { AutenticacaoHookProps } from './autenticacao.types';

const {
  authConfigs: {
    dados: { scope, authoritySignUp },
    extraQueryParameters,
  },
} = ConfigsManager.getInstance();

export const useAutenticacao = ({ dataFactory }: AutenticacaoHookProps) => {
  const {
    loginHint: loginHintProp,
    nome,
    isLead,
    idExterno,
    enturmacao,
    origem,
    possuiConta,
  } = useMemo(dataFactory, [dataFactory]);
  const { accounts, inProgress, instance } = useMsal();

  const loginHint = useMemo<string | null>(() => {
    return accounts.length ? (loginHintProp ?? accounts[0].username) : loginHintProp;
  }, [loginHintProp, accounts]);

  const logoutRedirectUri = useCallback(() => {
    if (isLead) {
      return instance.logout({ postLogoutRedirectUri: portal, logoutHint: loginHint ?? undefined });
    }
    return instance.logout({
      postLogoutRedirectUri: candidato,
      logoutHint: loginHint ?? undefined,
    });
  }, [instance, isLead]);

  const exceptionHandler = useCallback(
    async (e: ServerError) => {
      Assert.nonNullable(loginHint);

      if (e.errorCode === 'interaction_required') {
        await instance.loginRedirect({
          scopes: [scope],
          loginHint,
          prompt: 'login',
          extraQueryParameters,
        });
        return;
      }

      resultadoAutenticacaoAtualizado({
        autenticado: false,
        erro: e,
        pendente: false,
        token: null,
        idExterno,
      });
    },
    [instance, loginHint, logoutRedirectUri],
  );

  const acquireToken = useCallback(() => {
    if (loginHint && accounts.length > 0) {
      const conta = instance.getAccountByUsername(loginHint);

      if (conta) {
        instance.setActiveAccount(conta);
        Assert.notEmpty(scope);

        if (inProgress === InteractionStatus.None) {
          return instance
            .acquireTokenSilent({
              scopes: [scope],
              account: conta,
            })
            .then((r: AuthenticationResult) => {
              resultadoAutenticacaoAtualizado({
                autenticado: true,
                erro: null,
                pendente: false,
                token: r.accessToken ?? r.idToken,
                loginHint,
                isLead,
                idExterno,
                enturmacao,
                origem,
              });
            })
            .catch(exceptionHandler);
        }
      }
    }
  }, [accounts.length, exceptionHandler, inProgress, instance, loginHint]);

  const signIn = useCallback(async () => {
    Assert.notEmpty(scope);

    if (loginHint) {
      instance
        .ssoSilent({ scopes: [scope], loginHint, prompt: 'none' })
        .then(async (r: AuthenticationResult) => {
          // Validação se a conta resultante do SSO é realmente a conta correta
          // Necessário pelo comportamento de cache do MSAL
          if (r.account?.username.toLowerCase() === loginHint.toLowerCase()) {
            return await acquireToken();
          }

          await instance.logout();
        })
        .catch(exceptionHandler);
      return;
    }
    if (!instance.getActiveAccount() && accounts.length) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [loginHint, instance, exceptionHandler, acquireToken]);

  const getContaAtiva = useCallback(() => {
    if (loginHint) {
      return instance.getAccountByUsername(loginHint);
    }

    return null;
  }, [instance, loginHint, accounts]);

  const signOut = useCallback(() => {
    if (origem === 'APP_ALUNO') {
      instance.logout({ logoutHint: loginHint ?? undefined });
    } else {
      logoutRedirectUri();
    }
  }, [logoutRedirectUri, instance, loginHint, origem]);

  const signUp = useCallback(async () => {
    Assert.notEmpty(scope);

    if (loginHint) {
      instance
        .loginRedirect({
          authority: authoritySignUp,
          scopes: [scope],
          loginHint,
          prompt: 'login',
          extraQueryParameters: {
            ...extraQueryParameters,
            ...(origem && { origem }),
            ...(nome && { display_name: nome }),
          },
        })
        .catch((error) => {
          if (error.errorCode === 'interaction_in_progress') {
            return;
          }
        });
    }
  }, [instance, loginHint, origem, nome]);

  useEffect(() => {
    console.info({ message: 'Construindo a confirmação da account', loginHint, accountsLength: accounts.length });
    const pid = setInterval(() => {
      const localLoginHint = localStorage.getItem('login_hint');
      console.info({
        message: 'Executando a confirmação da account',
        localLoginHint,
        loginHint,
        accountsLength: accounts.length,
        accountsConectadas: accounts.map((a) => a.username).join(','),
      });
      if (!localLoginHint || localLoginHint === loginHint) {
        return;
      }
      const newAccount = instance.getAccountByUsername(localLoginHint);
      if (newAccount) {
        instance.setActiveAccount(newAccount);
        return;
      }
      signIn();
    }, 10000);
    return () => {
      console.info({
        message: 'Desconstruindo a execução da confirmação da account',
        loginHint,
        accountsLength: accounts.length,
      });
      clearInterval(pid);
    };
  }, [loginHint, instance, accounts, signIn]);

  return {
    loginHint,
    isLead,
    idExterno,
    possuiConta,
    signIn,
    signOut,
    signUp,
    acquireToken,
    getContaAtiva,
    inProgress,
  };
};
